// @flow

import * as React from "react";
import axios from 'axios';
import {withRouter} from "react-router-dom";
import {toast} from "react-toastify";
import arrow from "../images/arrow.png";
import LoadingScreen from "./LoadingScreen.react";
import FrozenSubscriptionBanner from "./FrozenSubscriptionBanner.react";
import {showGenericErrorToast, isPaymentDeclined, openPaymentMethodUpdate, startPlanCheckout, checkoutErrorMessage} from "../Utils";
import PlanCard, {formatPrice} from "./PlanCard.react";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

const UPGRADE_STATUS_TIMEOUT_MS = 45000;
const XHR_HEADERS = {headers: {"X-Requested-With": "XMLHttpRequest"}};
const swalInstance = withReactContent(Swal);

class Subscriptions extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            userSettings: {},
            allPlans: [],
            nowUpgradingPlanId: "",
            upgradeInterval: null,
            subscriptionsBillingType: 2,
        };
    }

    componentDidMount() {
        // Opens on the yearly plans, unless the company pays for a monthly plan: then on the monthly ones.
        let userSettings = {};
        let billingType = 2;
        axios.get(window.SERVER_URL + '/get_settings')
            .then((res) => {
                userSettings = res.data.result;
                billingType = userSettings.plan_id > 1 && userSettings.plan_billing_type === 1 ? 1 : 2;
                return axios.get(window.SERVER_URL + '/get_all_plans?billing_type=' + billingType);
            })
            .then((res) => {
                    this.setState({
                        isLoading: false,
                        userSettings: userSettings,
                        allPlans: res.data.result,
                        subscriptionsBillingType: billingType,
                    });
                }
            )
            .catch(() => {
                this.setState({isLoading: false});
                showGenericErrorToast();
            });

        this.successCallback = this.successCallback.bind(this);
        this.closeCallback = this.closeCallback.bind(this);
        this.toggleSubscriptionsBillingType = this.toggleSubscriptionsBillingType.bind(this);
        this.waitForPlan = this.waitForPlan.bind(this);
    }

    successCallback(data) {
        this.waitForPlan(data.product.id);
    }

    // Polls until the company is on newPlanId (set by Paddle's webhook), then goes to the dashboard.
    waitForPlan(newPlanId) {
        this.setState({nowUpgradingPlanId: newPlanId})
        const upgradeInterval = setInterval(() => {
            axios.post(window.SERVER_URL + '/check_upgrade_status', {newPlanId: newPlanId}).then((res) => {
                if (res.data.upgrade_status === true) {
                    clearInterval(upgradeInterval);
                    clearTimeout(upgradeTimeout);
                    this.props.history.push("/dashboard?showMessage=1");
                }
            });
        }, 1000);
        const upgradeTimeout = setTimeout(() => {
            clearInterval(upgradeInterval);
            this.setState({nowUpgradingPlanId: ""});
            toast.error("Your payment went through, but confirming it is taking longer than expected. Please refresh this page in a minute - if the plan still hasn't updated, contact support.");
        }, UPGRADE_STATUS_TIMEOUT_MS);
        this.state.upgradeInterval = upgradeInterval;
        this.state.upgradeTimeout = upgradeTimeout;
    }

    toggleSubscriptionsBillingType(newValue) {
        this.setState({subscriptionsBillingType: newValue});
        axios.get(window.SERVER_URL + '/get_all_plans?billing_type=' + newValue).then((response) => {
            if (response.status === 200) {
                this.setState({allPlans: response.data.result});
            }
        });
    }

    closeCallback() {
        clearInterval(this.state.upgradeInterval);
        clearTimeout(this.state.upgradeTimeout);
        this.setState({nowUpgradingPlanId: ""});
    }

    // Paid -> Free cancels the Paddle subscription and paid -> paid moves it to the new plan, both on the server.
    // Opening a new checkout for an already-paying company would create a second subscription and charge twice.
    changePlan(newPlanId) {
        if (newPlanId === 1) {
            this.switchToFreePlan();
            return;
        }

        if (this.state.userSettings.has_active_paid_subscription) {
            if (this.state.userSettings.is_active === false) {
                // Frozen: a plan change would charge the card that just failed (and most likely fail). The way out is
                // updating the payment method on the same subscription (the banner's button); never a second checkout.
                swalInstance.fire({
                    icon: "info",
                    title: "Reactivate your subscription first",
                    text: "Your last payment failed, so your subscription is frozen. Use \"Reactivate My Subscription\" in the red " +
                          "banner above to update your payment method. Once the payment goes through you can switch to any plan. " +
                          "(Choosing the Free plan cancels your subscription instead.)",
                    confirmButtonText: "OK",
                    confirmButtonColor: "#224514",
                });
                return;
            }
            this.switchPaidPlan(this.state.allPlans.find((plan) => plan.id === newPlanId));
            return;
        }

        // Not paying yet: a new Paddle checkout (the server signs which user/company it belongs to).
        startPlanCheckout(newPlanId, this.state.userSettings, (data) => this.successCallback(data), () => this.closeCallback())
            .catch((err) => toast.error(checkoutErrorMessage(err)));
    }

    switchToFreePlan() {
        const settings = this.state.userSettings;
        const requestFreePlan = () => axios.post(window.SERVER_URL + '/activate_user_free_plan', {}, XHR_HEADERS);

        if (!settings.has_active_paid_subscription) {
            // Nothing is being charged: either already cancelled (runs out its paid period) or no subscription at all.
            requestFreePlan().then((response) => {
                if (response.data.result === "already_cancelled") {
                    toast.info("Your paid plan is already cancelled. Your company moves to the Free plan on " + settings.subscription_cancellation_date + ".");
                } else {
                    this.props.history.push("/dashboard?showMessage=1");
                }
            }).catch(() => showGenericErrorToast());
            return;
        }

        swalInstance.fire({
            icon: "warning",
            title: "Cancel your paid plan?",
            text: "Your " + settings.plan_name + " subscription will be cancelled and your card won't be charged again. " +
                  "Your company keeps its current plan until the end of the period you've already paid for, then moves to the Free plan.",
            showCancelButton: true,
            cancelButtonText: "Keep my plan",
            confirmButtonText: "Yes, cancel my plan",
            confirmButtonColor: "red",
            // Stays open (spinner, no outside-click/Escape) until the server has cancelled it with Paddle; errors show inline.
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !swalInstance.isLoading(),
            allowEscapeKey: () => !swalInstance.isLoading(),
            // On failure: close the dialog and show the generic error toast (never the server's text).
            preConfirm: () => requestFreePlan().then(() => true).catch(() => {
                swalInstance.close();
                showGenericErrorToast();
                return false;
            }),
        }).then((result) => {
            if (result.isConfirmed) {
                swalInstance.fire({
                    icon: "success",
                    title: "Your paid plan has been cancelled",
                    text: "You won't be charged again. Your company moves to the Free plan when the current billing period ends.",
                    confirmButtonColor: "#224514",
                }).then(() => window.location.reload());
            }
        });
    }

    // The bank declined the charge for the plan change: nothing changed. Open Paddle's card-update page straight away,
    // with a message saying why (or, if it can't be opened, point to support).
    showPaymentDeclined() {
        const declined = "Your bank declined the charge for this plan change, so your plan wasn't changed.";
        if (openPaymentMethodUpdate(this.state.userSettings.update_url, () => toast.success("Payment method updated. Please choose your plan again."))) {
            toast.error(declined + " Please update your payment method, then try again.", {autoClose: 12000});
        } else {
            toast.error(declined + " Please contact support to update your payment method.", {autoClose: 12000});
        }
    }

    switchPaidPlan(newPlan) {
        const settings = this.state.userSettings;
        const period = newPlan.billing_type === 1 ? "month" : "year";
        swalInstance.fire({
            icon: "question",
            title: "Switch to " + newPlan.plan_name + "?",
            text: "Your " + settings.plan_name + " subscription will move to " + newPlan.plan_name + " ($" + formatPrice(newPlan.plan_price) + " per " + period + ") immediately. Upgrades are billed right away.",
            showCancelButton: true,
            confirmButtonText: "Switch plan",
            confirmButtonColor: "#224514",
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !swalInstance.isLoading(),
            allowEscapeKey: () => !swalInstance.isLoading(),
            preConfirm: () => axios.post(window.SERVER_URL + '/subscriptions/change_plan', {new_plan_id: newPlan.id}, XHR_HEADERS)
                .then(() => true)
                .catch((err) => {
                    swalInstance.close();
                    if (isPaymentDeclined(err)) {
                        this.showPaymentDeclined();
                    } else {
                        showGenericErrorToast();
                    }
                    return false;
                }),
        }).then((result) => {
            if (result.isConfirmed) {
                // Paddle has accepted the change; the company's plan flips when its subscription_updated webhook lands.
                this.waitForPlan(newPlan.id);
            }
        });
    }

    renderPlanButton(plan, isAdmin, secondary) {
        const isCurrent = this.state.userSettings.plan_id === plan.id;
        return (
            <button className={"btn " + (isCurrent || secondary ? "btn-outline" : "btn-primary")}
                    disabled={!isAdmin || isCurrent || this.state.nowUpgradingPlanId !== ""}
                    onClick={() => this.changePlan(plan.id)}>
                {isCurrent ? "Your Current Plan" : this.state.nowUpgradingPlanId === plan.id ? (
                    <span style={{display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px"}}>
                        <span className="btn-spinner" />
                        Please wait…
                    </span>
                ) : "Choose This Plan"}
            </button>
        );
    }

    // This billing period's QR invoices: used / allowance, what's left, and when the allowance resets.
    renderUsage() {
        const settings = this.state.userSettings;
        if (!settings || typeof settings.plan_max_qr_invoices_per_month !== "number") {
            return null;  // settings didn't load (an error toast is already shown): no misleading "0 / 0"
        }
        const allowance = settings.plan_max_qr_invoices_per_month;
        const used = typeof settings.qr_invoices_used_this_period === "number"
            ? settings.qr_invoices_used_this_period
            : Math.max(0, allowance - (settings.remaining_qr_invoices_this_month || 0));
        const remaining = Math.max(0, allowance - used);
        const percent = allowance > 0 ? Math.min(100, Math.round(used / allowance * 100)) : 100;
        const level = percent >= 100 ? " usage-progress-full" : percent >= 80 ? " usage-progress-high" : "";
        return (
            <React.Fragment>
                <h2 style={{margin: "40px 0 24px"}}>Usage</h2>
                <div className="app-card usage-card">
                    <div className="usage-card-header">
                        <div>
                            <div className="usage-card-label">QR invoices this billing period</div>
                            <div className="usage-card-value">{used} <span>/ {allowance}</span></div>
                        </div>
                        <span className="usage-card-plan">{(settings.plan_name || "").trim()}</span>
                    </div>
                    <div className="usage-progress-track usage-progress-large" role="progressbar" aria-valuemin={0}
                         aria-valuemax={allowance} aria-valuenow={Math.min(used, allowance)} aria-label="QR invoices used this billing period">
                        <div className={"usage-progress-fill" + level} style={{width: percent + "%"}} />
                    </div>
                    <div className="usage-card-footer">
                        <span><b>{used}</b> generated</span>
                        <span><b>{remaining}</b> remaining</span>
                        {settings.first_day_next_month ? <span>Resets on <b>{settings.first_day_next_month}</b></span> : null}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    render() {
        if (this.state.isLoading) {
            return <LoadingScreen />;
        }

        const isAdmin = this.state.userSettings.is_admin === true;
        const paidPlans = this.state.allPlans.filter((plan) => plan.id !== 1);
        const freePlan = this.state.allPlans.find((plan) => plan.id === 1);
        return (
            <div>
                <h2 style={{marginBottom: "24px"}}>Your subscription plan</h2>
                {this.state.userSettings.plan_id > 1 && this.state.userSettings.plan_is_offered === false ? (
                    // A legacy (old or custom) plan: it keeps billing and working, but it isn't sold any more, so it isn't
                    // in the list below. Tell the company which plan and price they're on and ask them to pick a new one.
                    <div className="app-info-banner" role="status">
                        <b>Your plan is a legacy plan.</b> Your company is currently subscribed to the
                        legacy <b>{this.state.userSettings.plan_name.trim()}</b>{/plan$/i.test(this.state.userSettings.plan_name.trim()) ? "" : " plan"}
                        {this.state.userSettings.plan_price ? <> for <b>${formatPrice(this.state.userSettings.plan_price)} per {this.state.userSettings.plan_billing_type === 2 ? "year" : "month"}</b></> : null}.
                        This plan is deprecated, so please choose one of the new plans on this page.
                        {isAdmin ? null : " Only a company admin can change the plan."}
                    </div>
                ) : null}
                {this.state.userSettings.is_active === false ? (
                    <FrozenSubscriptionBanner userSettings={this.state.userSettings} style={{marginBottom: "24px"}} />
                ) : null}
                {isAdmin ? null : (
                    <div className="app-card" style={{marginBottom: "24px"}}>Only a company admin can change the plan.</div>
                )}
                {this.state.userSettings.subscription_cancellation_date ? (
                    <div className="app-info-banner" role="status">
                        Your <b>{(this.state.userSettings.plan_name || "").trim()}</b> subscription is cancelled. Your company moves to the Free plan
                        on <b>{this.state.userSettings.subscription_cancellation_date}</b>.
                    </div>
                ) : null}
                <div className="billing-type-toggle">
                    <div style={{display: "inline-flex", border: "1px solid #d5dbe3", borderRadius: "8px", overflow: "hidden"}}>
                        <button className={"btn btn-sm" + (this.state.subscriptionsBillingType === 1 ? " btn-primary" : " btn-outline")}
                                style={{borderRadius: 0}} onClick={() => this.toggleSubscriptionsBillingType(1)}>Monthly</button>
                        <button className={"btn btn-sm" + (this.state.subscriptionsBillingType === 2 ? " btn-primary" : " btn-outline")}
                                style={{borderRadius: 0}} onClick={() => this.toggleSubscriptionsBillingType(2)}>Yearly (Get 2 months free <span role="img" aria-label="celebration">🎉</span>)</button>
                    </div>
                    <label className="billing-type-yearly-arrow"> <img src={arrow} alt="" /></label>
                </div>
                <div className="app-grid-row">
                    {paidPlans.map((plan) => (
                        <div className="app-grid-col plan-card-col" key={plan.id} style={{flex: "1 1 240px"}}>
                            <PlanCard plan={plan} button={this.renderPlanButton(plan, isAdmin, false)} />
                        </div>
                    ))}
                </div>
                {freePlan ? (
                    // Free sits underneath as a slim row, so the paid plans get the attention.
                    <div className="app-card plan-card-free">
                        <div className="plan-card-free-text">
                            <span className="plan-card-free-name">{freePlan.plan_name}</span>
                            <span className="plan-card-free-price">$0<span>/month</span></span>
                            <span className="plan-card-free-quota">Generate up to <b>{freePlan.max_qr_invoices} QR Invoices</b> every month</span>
                        </div>
                        {this.renderPlanButton(freePlan, isAdmin, true)}
                    </div>
                ) : null}

                {this.renderUsage()}
            </div>
        );
    }
}

export default withRouter(Subscriptions);